import React from "react";
import Info from "./Info";
import EditCard from "uicomponents/EditCard";
import { sectionsQuestions } from "pages/WorkersCompApplcation/utils/sectionsQuestions";

import { OwnerAndEmployeeCensusList } from "types/OwnerAndEmployeeCensus";
import {
  OfficeLocationList,
  SingleCensusType,
  SingleCensusInformationTypeKey,
  RemoteAddressType,
} from "types";
import styles from "../ReviewAndSubmit.module.css";
import { getOfficeFullAddress } from "utils/helper";
import { getMemberUuid } from "utils/idHelpers";

interface IOwnerEmployeeReviewProps {
  data: OwnerAndEmployeeCensusList;
  officeLocations: OfficeLocationList;
  setActiveStepIndex: (index: number) => void;
  sectionNumber: number;
}

type TDataEntries = [keyof SingleCensusType, string];

const OwnersAndEmployeesReview = ({
  data,
  officeLocations,
  setActiveStepIndex,
  sectionNumber,
}: IOwnerEmployeeReviewProps): JSX.Element => {
  if (!data) {
    return <></>;
  }

  const questions = Object.entries(
    sectionsQuestions.census.questions
  ) as TDataEntries[];
  const firstUserQuestionList = sectionsQuestions["census"].firstUserQuestions;

  return (
    <EditCard
      title={`${sectionsQuestions.census.title}`}
      onEditClick={() => setActiveStepIndex(sectionNumber)}
    >
      {data
        .filter(
          (employee) =>
            employee.isCompanyOwner === true ||
            (employee.isCompanyEmployee === true && employee.email)
        )
        .map((ownerOrEmployee, index) => {
          const isFirstUser = data[index]?.uuid === getMemberUuid();

          return (
            <React.Fragment key={ownerOrEmployee.uuid}>
              <span className={styles.subsectionTitle}>
                {index + 1}: {ownerOrEmployee.lastName},{" "}
                {ownerOrEmployee.firstName}
              </span>

              {questions.map(([key, value]) => {
                let questionText = value;

                if (isFirstUser) {
                  questionText =
                    firstUserQuestionList[
                      key as keyof typeof firstUserQuestionList
                    ] || value;
                }

                if (key === "firstName") {
                  const fullName = ownerOrEmployee.middleName
                    ? `${ownerOrEmployee.firstName} ${ownerOrEmployee.middleName} ${ownerOrEmployee.lastName}`
                    : `${ownerOrEmployee.firstName} ${ownerOrEmployee.lastName}`;

                  return (
                    <Info
                      key={"fullName"}
                      question="Full name"
                      answer={fullName}
                      questionFieldName={key}
                    />
                  );
                }

                if (key === "employeeType" || key === "email") {
                  return (
                    <Info
                      key={key}
                      question={questionText}
                      answer={ownerOrEmployee[key]}
                      questionFieldName={key}
                    />
                  );
                }

                if (
                  ownerOrEmployee.employeeInformation &&
                  key in ownerOrEmployee.employeeInformation
                ) {
                  const infoKey = key as SingleCensusInformationTypeKey;
                  const info = ownerOrEmployee.employeeInformation[infoKey];

                  return (
                    <Info
                      key={key}
                      question={questionText}
                      answer={info}
                      questionFieldName={key}
                    />
                  );
                }

                if (
                  ownerOrEmployee.employeeInformation?.isRemote &&
                  ownerOrEmployee.employeeInformation?.remoteAddress &&
                  key in ownerOrEmployee.employeeInformation.remoteAddress
                ) {
                  const remoteAddressKey = key as keyof RemoteAddressType;
                  const remoteAddress =
                    ownerOrEmployee.employeeInformation?.remoteAddress;

                  if (
                    remoteAddressKey === "address1" &&
                    remoteAddress[remoteAddressKey]
                  ) {
                    const remoteAddressAnswer =
                      getOfficeFullAddress(remoteAddress);

                    return (
                      <>
                        <Info
                          key={key}
                          question={"Remote location"}
                          answer={remoteAddressAnswer}
                          questionFieldName="remoteAddress"
                        />
                      </>
                    );
                  }
                }

                if (key === "officeLocationId") {
                  const officeLocation = officeLocations?.find(
                    (location) =>
                      location.uuid === ownerOrEmployee.officeLocationId
                  );

                  const address = officeLocation
                    ? getOfficeFullAddress(officeLocation)
                    : "";

                  return (
                    <Info
                      key={key}
                      question={questionText}
                      answer={address}
                      questionFieldName={key}
                    />
                  );
                }

                return <React.Fragment key={`${key}-${value}`} />;
              })}
            </React.Fragment>
          );
        })}
    </EditCard>
  );
};

export default OwnersAndEmployeesReview;
